import React, { useState } from "react";

import {
	Badge,
	Button,
	Card,
	SectionCard,
	Spinner,
	Toggle,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import SubscriptionService from "@/services/SubscriptionService";

import { useAuth } from "@/hooks/useAuth";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
	goesThroughPortal,
	usePlans,
	useSubscription,
} from "@/hooks/useSubscription";

// Full path, not the barrel: the barrel pulls the whole molecules layer in
// (the whole plan state) where this file only needs the gauges.
import { UsageGauges } from "@/components/molecules/UsageGauges/UsageGauges";

import {
	formatPrice,
	nextInvoiceLine,
	planFeatures,
	subscriptionStatusLine,
} from "@/utils/USubscription";
import { cn } from "@/utils/cn";

import {
	TSubscriptionState,
	TBillingInterval,
	TPlan,
} from "@/types/TSubscription";

import { errorToast } from "@/modules/index";

const PlanCard: React.FC<{
	plan: TPlan;
	interval: TBillingInterval;
	current: boolean;
	/** Cheaper than the plan in force: a downgrade, through the portal. */
	below: boolean;
	/** A paying customer: every change goes through the portal. */
	viaPortal: boolean;
	onChoose: (plan: TPlan) => void;
	busy: boolean;
}> = ({ plan, interval, current, below, viaPortal, onChoose, busy }) => {
	const { user } = useAuth();
	const price = interval === "month" ? plan.priceMonth : plan.priceYear;
	const purchasable = plan.code !== "free" && price !== null;
	return (
		<Card
			className={cn(["flex flex-col gap-3", current && "border-primary"])}
			data-testid={`plan-card-${plan.code}`}
		>
			<div className="flex items-center justify-between">
				<span className="text-lg font-bold">{plan.name}</span>
				{current && (
					<Badge color="primary" variant="tint" size="sm">
						{t("subscription.plans.currentPlan")}
					</Badge>
				)}
			</div>
			<div className="text-2xl font-bold">
				{plan.code === "free" ? (
					formatPrice(0, plan.currency)
				) : price === null ? (
					<span className="text-base font-normal text-neutral-500">
						{t("subscription.plans.priceUnset")}
					</span>
				) : (
					<>
						{formatPrice(price, plan.currency)}
						<span className="text-sm font-normal text-neutral-500">
							{" "}
							{interval === "month"
								? t("subscription.plans.perMonth")
								: t("subscription.plans.perYear")}
						</span>
					</>
				)}
			</div>
			<ul className="flex grow flex-col gap-1 text-sm">
				{planFeatures(plan, user?.features).map((feature) => (
					<li key={feature}>· {feature}</li>
				))}
			</ul>
			{purchasable && !current && (
				<Button
					color={below ? "neutral" : "primary"}
					variant={below ? "outline" : "solid"}
					size="sm"
					isLoading={busy}
					onClick={() => onChoose(plan)}
				>
					{!viaPortal
						? t("subscription.plans.choose")
						: below
							? t("subscription.plans.downgrade")
							: t("subscription.plans.upgradeTo", {
									plan: plan.name,
								})}
				</Button>
			)}
		</Card>
	);
};

/**
 * The "my subscription" card: the plan in force, where the subscription
 * stands, the next invoice and the usage gauges. On /subscription with
 * the portal button as its action; on the profile with a link to
 * /subscription (SubscriptionSummary).
 */
export const SubscriptionCurrentCard: React.FC<{
	state: TSubscriptionState;
	actions?: React.ReactNode;
	className?: string;
}> = ({ state, actions, className }) => {
	const { plan, subscription, usage } = state;
	const status = subscriptionStatusLine(subscription, plan.code);
	const nextInvoice = nextInvoiceLine(subscription, plan);

	return (
		<SectionCard
			title={t("subscription.current.title")}
			actions={actions}
			className={className}
		>
			<div className="flex flex-col gap-5">
				<div className="flex flex-wrap items-center gap-3">
					<span className="text-neutral-500">
						{t("subscription.current.plan")}
					</span>
					<span
						className="text-xl font-bold"
						data-testid="current-plan"
					>
						{plan.name}
					</span>
					<Badge
						color={status.color}
						variant="tint"
						data-testid="subscription-status"
					>
						{status.text}
					</Badge>
				</div>
				{nextInvoice && (
					<p
						className="text-sm text-neutral-500"
						data-testid="next-invoice"
					>
						{nextInvoice}
					</p>
				)}
				<UsageGauges usage={usage} />
			</div>
		</SectionCard>
	);
};

/**
 * The profile's subscription block: where the subscription stands, how
 * much of the plan is used, and the plans on offer. Checkout and the
 * portal are Stripe's pages: the buttons send the browser there.
 */
export const SubscriptionSection: React.FC<{
	/** Another workspace than the user's own (the /subscription page's choice). */
	workspaceId?: number;
}> = ({ workspaceId }) => {
	const { state, isLoading, error } = useSubscription(workspaceId);
	const plans = usePlans();
	// Sharing paused: the Team plans sell nothing else - off the grid,
	// unless it is the plan in force - and Enterprise goes with them.
	const sharingOn = useFeatureFlag("sharing");
	const [interval, setInterval] = useState<TBillingInterval>("month");
	const [busy, setBusy] = useState<string | null>(null);

	const go = (
		promise: Promise<string>,
		errorMessage: string,
		key: string
	) => {
		setBusy(key);
		const prom = promise
			.then((url) => {
				window.location.assign(url);
			})
			.finally(() => setBusy(null));
		errorToast(prom, errorMessage);
	};

	// A paying customer changes plan in the portal (Stripe prorates);
	// everyone else buys through Checkout.
	const choose = (plan: TPlan) =>
		goesThroughPortal(state?.subscription ?? null)
			? go(
					SubscriptionService.portal(workspaceId),
					t("subscription.errors.portal"),
					plan.code
				)
			: go(
					SubscriptionService.checkout(
						plan.code,
						interval,
						workspaceId
					),
					t("subscription.errors.checkout"),
					plan.code
				);

	const portal = () =>
		go(
			SubscriptionService.portal(workspaceId),
			t("subscription.errors.portal"),
			"portal"
		);

	if (isLoading || plans.isLoading)
		return <Spinner className="mx-auto my-6" />;
	if (error || !state) return null;

	return (
		<SubscriptionSectionView
			state={state}
			plans={(plans.data ?? []).filter(
				(plan) =>
					sharingOn || !plan.canShare || plan.code === state.plan.code
			)}
			showEnterprise={sharingOn}
			interval={interval}
			onInterval={setInterval}
			busy={busy}
			onChoose={choose}
			onPortal={portal}
		/>
	);
};

/** The markup, given the data — what the tests render. */
export const SubscriptionSectionView: React.FC<{
	state: TSubscriptionState;
	plans: TPlan[];
	/** The on-quote Enterprise card, off while sharing is paused. */
	showEnterprise: boolean;
	interval: TBillingInterval;
	onInterval: (interval: TBillingInterval) => void;
	busy: string | null;
	onChoose: (plan: TPlan) => void;
	onPortal: () => void;
}> = ({
	state,
	plans,
	showEnterprise,
	interval,
	onInterval,
	busy,
	onChoose,
	onPortal,
}) => {
	const { plan, subscription } = state;
	const viaPortal = goesThroughPortal(subscription);
	const currentIndex = plans.findIndex((p) => p.code === plan.code);

	return (
		<div id="subscription" className="flex flex-col gap-8">
			<SubscriptionCurrentCard
				state={state}
				actions={
					subscription?.hasCustomer && (
						<Button
							variant="outline"
							size="sm"
							isLoading={busy === "portal"}
							onClick={onPortal}
							title={t("subscription.current.manageHint")}
						>
							{t("subscription.current.manage")}
						</Button>
					)
				}
			/>

			<SectionCard
				title={t("subscription.plans.title")}
				actions={
					<div className="flex items-center gap-2 text-sm">
						<span>{t("subscription.plans.monthly")}</span>
						<Toggle
							checked={interval === "year"}
							onChange={(checked) =>
								onInterval(checked ? "year" : "month")
							}
							aria-label={t("subscription.plans.yearly")}
						/>
						<span>{t("subscription.plans.yearly")}</span>
					</div>
				}
			>
				<div className="flex flex-col gap-4">
					<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
						{plans
							.filter((candidate) => candidate.code !== "free")
							.map((candidate, _index, list) => (
								<PlanCard
									key={candidate.code}
									plan={candidate}
									interval={interval}
									current={candidate.code === plan.code}
									below={
										currentIndex >= 0 &&
										plans.indexOf(candidate) < currentIndex
									}
									viaPortal={viaPortal && list.length > 0}
									onChoose={onChoose}
									busy={busy === candidate.code}
								/>
							))}
						{showEnterprise && (
							<Card
								className="flex flex-col gap-3 border-dashed"
								data-testid="plan-card-enterprise"
							>
								<span className="text-lg font-bold">
									{t("subscription.plans.enterprise")}
								</span>
								<div className="text-2xl font-bold">
									{t("subscription.plans.onQuote")}
								</div>
								<ul className="flex grow flex-col gap-1 text-sm">
									<li>
										·{" "}
										{t(
											"subscription.plans.features.membersUnlimited"
										)}
									</li>
									<li>
										·{" "}
										{t("subscription.plans.enterpriseSso")}
									</li>
								</ul>
								<span className="text-sm text-neutral-500">
									{t("subscription.plans.enterpriseContact")}
								</span>
							</Card>
						)}
					</div>
					<p className="text-xs text-neutral-500">
						{t("subscription.plans.footnote")}
					</p>
				</div>
			</SectionCard>
		</div>
	);
};
